import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Reads a swap QR with the camera and makes you friends straight away
export const ScanCode = ({ onDone }) => {
  const video = useRef(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    let stream;
    let raf = 0;
    let done = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const found = async (text) => {
      const m = text.match(/\/swap\/([\w-]+)/);
      if (!m) return false;
      done = true;
      try {
        const r = await api.swapAccept(m[1]);
        toast.success(`You and ${r.name.split(" ")[0]} are now friends`);
        qc.invalidateQueries({ queryKey: ["friends"] });
        onDone?.();
        navigate(`/friend/${r.friend_id}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't add them");
        done = false;
      }
      return true;
    };
    const tick = () => {
      const v = video.current;
      if (!done && v && v.readyState >= 2) {
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0);
        const code = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height, { inversionAttempts: "dontInvert" });
        if (code?.data) found(code.data);
      }
      raf = requestAnimationFrame(tick);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((s) => { stream = s; video.current.srcObject = s; video.current.play(); raf = requestAnimationFrame(tick); })
      .catch(() => setErr("Allow camera access to scan, or ask them to scan your code instead."));
    return () => { cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [navigate, onDone, qc]);

  return (
    <div className="space-y-3" data-testid="swap-scanner">
      <div className="relative mx-auto aspect-square w-full max-w-[280px] overflow-hidden rounded-2xl bg-black">
        <video ref={video} playsInline muted className="h-full w-full object-cover" data-testid="swap-scanner-video" />
        <div className="pointer-events-none absolute inset-8 rounded-xl border-2 border-emerald-300/80" />
      </div>
      {err ? <p className="text-center text-sm text-rose-300" data-testid="swap-scanner-error">{err}</p>
        : <p className="text-center text-sm text-slate-300">Point the camera at their bucketlist QR code.</p>}
    </div>
  );
};
